import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Divider, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type HealthIndexReason = string;

export type ScoreBreakdownEntry = {
    value?: number | null;
    description?: string | null;
};

export type ScoreBreakdownMap = Record<string, ScoreBreakdownEntry | undefined>;

export interface HealthIndexOverviewProps {
    storageName: string;
    finalHealthIndex?: number | null;
    reasons?: HealthIndexReason[] | null;
    inspectionDate?: string | null;
    scoreBreakdown?: ScoreBreakdownMap | null;
    onViewWorkHistory?: (() => void) | null;
    viewWorkHistoryDisabled?: boolean;
}

const getHealthIndexColor = (value: number | null | undefined, theme: Theme) => {
    if (value === null || value === undefined) {
        return theme.palette.grey[400];
    }

    if (value >= 80) {
        return theme.palette.success.main;
    }

    if (value >= 60) {
        return theme.palette.warning.main;
    }

    if (value >= 40) {
        return theme.palette.warning.dark;
    }

    return theme.palette.error.main;
};

const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }
    return `${value.toFixed(0)}%`;
};

const formatInspectionDate = (value: string | null | undefined) => {
    if (!value) return 'No recent inspection';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Last inspection unknown';
    return `Last inspection: ${date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })}`;
};

const HealthIndexOverview: React.FC<HealthIndexOverviewProps> = ({
    storageName,
    finalHealthIndex,
    reasons,
    inspectionDate,
    scoreBreakdown,
    onViewWorkHistory,
    viewWorkHistoryDisabled,
}) => {
    const breakdownItems = useMemo(() => {
        if (!scoreBreakdown) return [] as Array<{ label: string; value: string; description: string }>;

        const labelMap: Record<string, string> = {
            breakdown: 'Breakdown',
            deltaT: 'Delta T',
            sprouting: 'Sprouting',
            moisture: 'Moisture',
            bugs: 'Bugs',
            odors: 'Odors',
            liquid: 'Liquid',
            co2: 'CO₂',
            osaControl: 'OSA control',
        };

        const entries: Array<{ label: string; value: string; description: string }> = [];

        Object.entries(scoreBreakdown).forEach(([key, detail]) => {
            if (!detail) return;
            const numeric = typeof detail.value === 'number' ? detail.value : null;
            const label = labelMap[key] ?? key;
            const value = numeric !== null ? `${numeric.toFixed(0)}%` : '—';
            const description = detail.description || 'No notes provided';
            entries.push({ label, value, description });
        });

        return entries.sort((a, b) => a.label.localeCompare(b.label));
    }, [scoreBreakdown]);

    const handleBreakdownNavigate = useCallback((event: React.SyntheticEvent) => {
        if (!onViewWorkHistory || viewWorkHistoryDisabled) return;
        event.preventDefault();
        event.stopPropagation();
        onViewWorkHistory();
    }, [onViewWorkHistory, viewWorkHistoryDisabled]);

    const handleBreakdownKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onViewWorkHistory || viewWorkHistoryDisabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
            handleBreakdownNavigate(event);
        }
    }, [handleBreakdownNavigate, onViewWorkHistory, viewWorkHistoryDisabled]);

    const interactive = Boolean(onViewWorkHistory);

    return (
        <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                    Storage Health — {storageName}
                </Typography>
                <Tooltip title="The health index reflects recent inspection data and key risk factors.">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
            </Box>

            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Overall Health Index
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {formatPercentage(finalHealthIndex)}
                    </Typography>
                </Box>
                <Tooltip
                    placement="bottom"
                    disableFocusListener={breakdownItems.length === 0}
                    disableHoverListener={breakdownItems.length === 0}
                    title={
                        breakdownItems.length ? (
                            <Paper
                                elevation={0}
                                onClick={handleBreakdownNavigate}
                                onKeyDown={handleBreakdownKeyDown}
                                role={interactive ? 'button' : undefined}
                                tabIndex={interactive ? 0 : undefined}
                                aria-disabled={viewWorkHistoryDisabled ? 'true' : undefined}
                                sx={(theme) => ({
                                    p: 1.5,
                                    minWidth: { xs: 260, sm: 400 },
                                    cursor: interactive
                                        ? (viewWorkHistoryDisabled ? 'not-allowed' : 'pointer')
                                        : 'default',
                                    opacity: interactive && viewWorkHistoryDisabled ? 0.65 : 1,
                                    outline: 'none',
                                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
                                    '&:focus-visible': {
                                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`
                                    },
                                    ...(interactive ? {
                                        '&:hover': {
                                            boxShadow: viewWorkHistoryDisabled
                                                ? 'none'
                                                : `0 0 0 2px ${theme.palette.primary.main}22, 0 6px 18px -8px ${theme.palette.primary.main}55`,
                                            border: viewWorkHistoryDisabled ? undefined : `1px solid ${theme.palette.primary.main}33`
                                        },
                                        '&:active': viewWorkHistoryDisabled ? {} : {
                                            boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`
                                        }
                                    } : {})
                                })}
                            >
                                <Typography variant="subtitle2" gutterBottom>
                                    Score breakdown
                                </Typography>
                                <Stack spacing={1.5}>
                                    <Box
                                        display="grid"
                                        gap={1.5}
                                        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }}
                                    >
                                        {breakdownItems.map((item) => (
                                            <Box key={item.label}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.value}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    {interactive ? (
                                        <>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {viewWorkHistoryDisabled
                                                    ? 'No linked inspection sheet is available yet.'
                                                    : 'Click to view the inspection sheet that produced this score.'}
                                            </Typography>
                                        </>
                                    ) : null}
                                </Stack>
                            </Paper>
                        ) : (
                            ''
                        )
                    }
                    enterDelay={150}
                    leaveDelay={100}
                    arrow
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: { xs: 340, sm: 720 },
                                px: 0,
                                py: 0,
                            }
                        }
                    }}
                >
                    <Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.max(0, Math.min(100, finalHealthIndex ?? 0))}
                            sx={{
                                height: 12,
                                borderRadius: 6,
                                backgroundColor: (theme) => theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 6,
                                    backgroundColor: (theme) => getHealthIndexColor(finalHealthIndex ?? null, theme),
                                },
                            }}
                        />
                    </Box>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                    {formatInspectionDate(inspectionDate)}
                </Typography>
            </Box>

            <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                    Key factors impacting score
                </Typography>
                {reasons && reasons.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {reasons.map((reason, index) => (
                            <Chip key={`${reason}-${index}`} label={reason} size="small" color="default" variant="outlined" sx={{ mb: 0.5 }} />
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No concerns detected. Conditions look stable.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

export default HealthIndexOverview;
